// Vendor receipt signing for outsourcing manifests (jobq-api 2026-10-29, main.py "VENDOR RECEIPT SIGNING"; behind
// GET /features shipping_signing.vendor_receipt). When a KC-SVC paper is SENT, jobq-api starts ONE code for it and the
// PRINTED paper carries its QR ("Vendor: scan with any phone camera to sign for receipt"); the vendor's receiver signs
// on their own phone at their dock (features/signing/SignPortal, kind 'vendor_receipt'). The emailed paper never has it.
//
//   POST /outside_processing_orders/{id}/send               the answer gains vendor_sign (the link view: request_id, url,
//                                                            short_code, qr_svg, expires_at) or vendor_sign_error
//   GET  /outside_processing_orders/{id}/vendor_signing     status none | pending | expired | void | signed, signed {name,
//                                                            at, has_problem, problems, received_note}, document_url,
//                                                            photos, and while pending the QR again (the printed code)
//   POST /outside_processing_orders/{id}/vendor_signing     {pin, reissue}: the paper's code (one for a paper sent before
//                                                            this went live); reissue = void it, make a new one
//   GET  /outside_processing_orders (+ /{id})               each paper: vendor_signed_at / vendor_signed_by, and
//                                                            vendor_sign {request_id, status, short_code, expires_at}
//
// ONE copy of these rules: the Outsourcing operator mode (Shipping hub) and the coating desk's At Vendor tab both use
// them. Every call resolves {ok, status, body, message} and never throws.
import { detailToString } from '../../utils/apiError';
import { clockText } from './signRequestsApi';

async function call(url, { method = 'GET', body } = {}) {
  try {
    const opts = { method };
    if (body !== undefined) {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(url, opts);
    let data = null;
    try { data = await res.json(); } catch (_) { data = null; }
    if (!res.ok) {
      const d = data && typeof data === 'object' ? data.detail : null;
      const msg = d && typeof d === 'object' && d.message ? d.message : detailToString(d);
      return { ok: false, status: res.status, body: data, message: msg || `HTTP ${res.status}` };
    }
    return { ok: true, status: res.status, body: data, message: '' };
  } catch (_) {
    return { ok: false, status: 0, body: null, message: 'Could not reach JobQ. Check the connection and try again.' };
  }
}

export function readVendorSigning(apiBase, opoId) {
  return call(`${apiBase}/outside_processing_orders/${opoId}/vendor_signing`);
}

export function startVendorSigning(apiBase, opoId, { pin, reissue = false }) {
  return call(`${apiBase}/outside_processing_orders/${opoId}/vendor_signing`, {
    method: 'POST', body: { pin, reissue: !!reissue },
  });
}

/** True when vendor signing is on (GET /features shipping_signing.vendor_receipt === true). */
export function vendorSigningOn(signing) {
  return !!(signing && signing.vendor_receipt === true);
}

const AT_VENDOR = new Set(['Sent', 'Partially Returned']);

/**
 * What the paper says about the vendor's signature, for a chip: {state, text, tone} or null (nothing to say).
 *   signed   "Signed at the vendor · Dana Ruiz · Oct 29 2:14 PM" (success; shown even with signing switched off)
 *   pending  "Waiting for vendor signature" (warning)
 *   none     "No vendor signing code" (secondary): a paper sent before this went live, or the code ran out / was
 *            cancelled; the button starts one
 * Only papers at the vendor (or back, once signed) say anything.
 */
export function vendorSignChip(opo, { on = true, now = new Date() } = {}) {
  if (!opo) return null;
  if (opo.vendor_signed_at) {
    const who = opo.vendor_signed_by ? ` · ${opo.vendor_signed_by}` : '';
    const when = clockText(opo.vendor_signed_at, now);
    const problem = opo.vendor_sign && opo.vendor_sign.has_problem;
    return { state: 'signed', text: `Signed at the vendor${who}${when ? ` · ${when}` : ''}`, tone: 'success', problem: !!problem };
  }
  if (!on || !AT_VENDOR.has(opo.status)) return null;
  const s = opo.vendor_sign;
  if (s && s.status === 'pending') return { state: 'pending', text: 'Waiting for vendor signature', tone: 'warning' };
  return { state: 'none', text: 'No vendor signing code', tone: 'secondary' };
}

/** The sentence after a send: what happened to the vendor's code. '' = nothing to say. */
export function sendSignNote(data) {
  const d = data || {};
  if (d.vendor_signed && d.vendor_signed.name) return `The vendor already signed for this paper (${d.vendor_signed.name}).`;
  if (d.vendor_sign && d.vendor_sign.short_code) {
    return `The printed paper carries the vendor's signing QR (code ${d.vendor_sign.short_code}).`;
  }
  if (d.vendor_sign_error) return d.vendor_sign_error;
  return '';
}
